import DataGrid from '@components/common/DataGrid';
import ErrorPage from '@components/common/ErrorPage';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Header from '@components/common/layout/Header';
import PropTypes from 'prop-types';
import React, {useCallback, useEffect, useRef, useState} from 'react';
import SearchIcon from '@mui/icons-material/Search';
import routePath, {Routes} from '@account/utils/route';
import {
  Box,
  Chip,
  Collapse,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {Link as RouterLink, useHistory, useLocation} from 'react-router-dom';
import {customerAccountSearchParams} from '@account/utils/customer-account-search';
import {displayDate} from '@account/utils/dates';
import {hasRoleLevel, roleLevels} from '@account/utils';
import {programTypeLabel} from '@account/utils/programs';
import {useAuth} from '@apps-common/auth-react';
import {useCustomerAccounts} from '@hooks/data/ims/useCustomerAccounts';
import {useFlags} from '@hooks/useFlags';

// Lifecycle filter for the accounts list. Defaults to 'active' so the page shows
// current accounts, not the long tail of expired ones. Values map to the
// backend status filter on GET /customer-accounts/.
const STATUS_OPTIONS = [
  {value: 'active', label: 'Active'},
  {value: 'expired', label: 'Expired'},
  {value: 'disabled', label: 'Disabled'},
];

export const RECENT_KEY = 'accounts_recent_v1';
// Persist the collapsed state of the Recently viewed panel so it stays how the
// user left it across visits.
const RECENT_COLLAPSED_KEY = 'accounts_recent_collapsed_v1';

export const readRecentCollapsed = () => {
  try {
    return localStorage.getItem(RECENT_COLLAPSED_KEY) === '1';
  } catch (_) {
    return false;
  }
};

export const writeRecentCollapsed = collapsed => {
  try {
    localStorage.setItem(RECENT_COLLAPSED_KEY, collapsed ? '1' : '0');
  } catch (_) {
    // Non-fatal: private mode / storage disabled — the panel just won't persist.
  }
};
const EM_DASH = '—';

// Split an email into [local, "@domain"] so the domain can be de-emphasized in
// the Account Name column. Non-email values return [value, ''].
export const splitEmail = value => {
  const at = typeof value === 'string' ? value.indexOf('@') : -1;
  return at > 0 ? [value.slice(0, at), value.slice(at)] : [value, ''];
};

// Prepend `row` to the recently-viewed list in localStorage, de-duping by id
// and capping at 5. Returns the updated array, or null if storage is
// unavailable / corrupt. Shared by the row-click handler and the name link so
// both navigation paths record a visit — the name link stops propagation to
// avoid a double navigation, which also blocks the row handler, so it records
// the visit itself.
export const recordRecent = row => {
  try {
    const prev = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
    const updated = [
      {id: row.id, name: row.name, sfdc_id: row.sfdc_id},
      ...prev.filter(r => r.id !== row.id),
    ].slice(0, 5);
    localStorage.setItem(RECENT_KEY, JSON.stringify(updated));
    return updated;
  } catch (_) {
    return null;
  }
};

const columns = [
  {
    field: 'name',
    headerName: 'Account Name',
    flex: 1.25,
    sortable: false,
    renderCell: params => {
      const [local, domain] = splitEmail(params.value);
      return (
        <Link
          component={RouterLink}
          to={routePath(Routes.ACCOUNT_DETAIL, {accountId: params.row.id})}
          variant="body2"
          onClick={e => {
            e.stopPropagation();
            recordRecent(params.row);
          }}
          underline="hover"
        >
          {local}
          {domain && (
            <Box component="span" sx={{color: 'text.disabled'}}>
              {domain}
            </Box>
          )}
        </Link>
      );
    },
  },
  {
    field: 'sfdc_id',
    headerName: 'SFDC ID',
    flex: 0.75,
    sortable: false,
    cellClassName: 'mono-cell',
    valueGetter: params => params.value || EM_DASH,
  },
  {
    field: 'program_id',
    headerName: 'Type',
    flex: 0.5,
    sortable: false,
    // Prefer the backend-resolved program_name (from the account's default
    // workspace, pda-admin !3402); fall back to the hardcoded map until that
    // ships everywhere.
    valueGetter: params =>
      params.row.program_name || programTypeLabel(params.row.program_id),
  },
  {
    field: 'created_at',
    headerName: 'Created',
    flex: 0.6,
    cellClassName: 'tnum-cell',
    valueGetter: params =>
      params.row?.created_at ? displayDate(params.row.created_at) : EM_DASH,
  },
];

// Accounts list is server-paginated, so sorting must be server-side (a
// client sort would only reorder the current page). Map the grid's sortModel
// to the API's ?sort=(-)field param; default to newest-first.
export const sortModelToParam = model => {
  const s = model?.[0];
  if (!s) return '-created_at';
  return `${s.sort === 'desc' ? '-' : ''}${s.field}`;
};

const readRecent = () => {
  try {
    return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
  } catch (_) {
    return [];
  }
};

const RecentAccounts = ({recent, onOpen}) => {
  const [collapsed, setCollapsed] = useState(readRecentCollapsed);

  const toggleCollapsed = useCallback(() => {
    setCollapsed(prev => {
      const next = !prev;
      writeRecentCollapsed(next);
      return next;
    });
  }, []);

  if (!recent.length) return null;

  return (
    <Box
      mt={2}
      mb={3}
      sx={{
        border: theme => `1px solid ${theme.palette.grey[200]}`,
        borderRadius: 1.5,
        bgcolor: 'grey.50',
        px: 2,
        py: 1.5,
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        onClick={toggleCollapsed}
        sx={{cursor: 'pointer'}}
      >
        <Typography
          variant="caption"
          color="text.secondary"
          fontWeight={600}
          sx={{textTransform: 'uppercase', letterSpacing: 0.5}}
        >
          Recently viewed
        </Typography>
        <IconButton
          size="small"
          aria-label={
            collapsed ? 'Expand recently viewed' : 'Collapse recently viewed'
          }
          aria-expanded={!collapsed}
          onClick={e => {
            // The whole header toggles; stop the button from double-firing it.
            e.stopPropagation();
            toggleCollapsed();
          }}
        >
          {collapsed ? (
            <ExpandMoreIcon fontSize="small" />
          ) : (
            <ExpandLessIcon fontSize="small" />
          )}
        </IconButton>
      </Box>
      <Collapse in={!collapsed}>
        <Box mt={1} display="flex" flexDirection="column" gap={0.75}>
          {recent.map(r => (
            <Box
              key={r.id}
              display="flex"
              alignItems="center"
              gap={1}
              sx={{cursor: 'pointer', '&:hover': {opacity: 0.7}}}
              onClick={() => onOpen(r.id)}
            >
              <Typography variant="body2" fontWeight={500}>
                {r.name}
              </Typography>
              {r.sfdc_id && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontFamily="monospace"
                  sx={{fontVariantNumeric: 'tabular-nums'}}
                >
                  {r.sfdc_id}
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

RecentAccounts.propTypes = {
  recent: PropTypes.array.isRequired,
  onOpen: PropTypes.func.isRequired,
};

const NewAccountsPage = () => {
  const auth = useAuth();
  const history = useHistory();
  const location = useLocation();
  const searchRef = useRef(null);
  const {showAllAccounts} = useFlags();
  const isPlanetAdmin = hasRoleLevel(auth, [roleLevels.planet_admin]);

  const urlParams = new URLSearchParams(location.search);
  const [search, setSearch] = useState(urlParams.get('q') || '');
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [page, setPage] = useState(0);
  const [sortModel, setSortModel] = useState([
    {field: 'created_at', sort: 'desc'},
  ]);
  const [showTrials, setShowTrials] = useState(false);
  const [status, setStatus] = useState('active');
  // Seeded once from localStorage; navigating to an account unmounts this page,
  // so the list re-reads on return rather than updating in place.
  const [recent] = useState(readRecent);
  const pageSize = 25;

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(0);
      const p = new URLSearchParams(location.search);
      if (search) p.set('q', search);
      else p.delete('q');
      history.replace({search: p.toString()});
    }, 250);
    return () => clearTimeout(t);
  }, [search, history, location.search]);

  useEffect(() => {
    const handler = e => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        searchRef.current?.focus();
        searchRef.current?.select();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const {data, isLoading, isFetching} = useCustomerAccounts(
    {
      limit: pageSize,
      offset: page * pageSize,
      sort: sortModelToParam(sortModel),
      ...customerAccountSearchParams(debouncedSearch),
      is_trial: showTrials ? undefined : false,
      status,
    },
    {
      enabled: isPlanetAdmin && showAllAccounts,
      keepPreviousData: true,
    }
  );

  const results = data?.results ?? [];
  const total = data?.meta?.count ?? 0;
  const handleRowClick = useCallback(
    params => {
      recordRecent(params.row);
      history.push(routePath(Routes.ACCOUNT_DETAIL, {accountId: params.id}));
    },
    [history]
  );

  if (!showAllAccounts || !isPlanetAdmin) return <ErrorPage statusCode={404} />;

  return (
    <>
      <Header name="All Accounts" />

      <Box mb={2}>
        <TextField
          inputRef={searchRef}
          autoFocus
          fullWidth
          size="small"
          placeholder="Search by name, email, or SFDC ID"
          value={search}
          onChange={e => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <Box
                  component="span"
                  sx={{
                    px: 0.75,
                    py: 0.25,
                    border: theme => `1px solid ${theme.palette.grey[300]}`,
                    borderRadius: 1,
                    fontFamily: 'monospace',
                    fontSize: 11,
                    lineHeight: 1.4,
                    color: 'text.disabled',
                  }}
                >
                  ⌘K
                </Box>
              </InputAdornment>
            ),
          }}
          sx={{'& .MuiOutlinedInput-root': {borderRadius: 2}}}
        />
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mt={0.5}
        >
          <Typography variant="caption" color="text.secondary">
            {!isLoading &&
              `${total.toLocaleString()} account${total !== 1 ? 's' : ''}${!showTrials ? ' · trials hidden' : ''}${isFetching ? ' · updating…' : ''}`}
          </Typography>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Stack direction="row" spacing={0.75}>
              {STATUS_OPTIONS.map(opt => (
                <Chip
                  key={opt.value}
                  label={opt.label}
                  size="small"
                  variant={status === opt.value ? 'filled' : 'outlined'}
                  color={status === opt.value ? 'primary' : 'default'}
                  onClick={() => {
                    setStatus(opt.value);
                    setPage(0);
                  }}
                  aria-pressed={status === opt.value}
                />
              ))}
            </Stack>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={showTrials}
                  onChange={e => {
                    setShowTrials(e.target.checked);
                    setPage(0);
                  }}
                />
              }
              label={
                <Typography variant="caption" color="textSecondary">
                  Show trials
                </Typography>
              }
            />
          </Box>
        </Box>
      </Box>

      {!search && (
        <RecentAccounts
          recent={recent}
          onOpen={id =>
            history.push(routePath(Routes.ACCOUNT_DETAIL, {accountId: id}))
          }
        />
      )}

      <DataGrid
        columns={columns}
        sortingMode="server"
        sortModel={sortModel}
        onSortModelChange={model => {
          setSortModel(
            model.length ? model : [{field: 'created_at', sort: 'desc'}]
          );
          setPage(0);
        }}
        loading={isLoading}
        onPaginationModelChange={model => setPage(model.page)}
        onRowClick={handleRowClick}
        pagination
        paginationMode="server"
        paginationModel={{page, pageSize}}
        rowCount={total}
        rows={results}
        localeText={{
          MuiTablePagination: {
            labelDisplayedRows: ({from, to, count}) =>
              `${from.toLocaleString()}–${to.toLocaleString()} of ${count.toLocaleString()}`,
          },
        }}
        slotProps={{
          noRowsOverlay: {message: 'No accounts found'},
          noResultsOverlay: {message: 'No results'},
        }}
        sx={{
          '& .MuiDataGrid-row': {cursor: 'pointer'},
          '& .mono-cell': {
            fontFamily: 'monospace',
            fontVariantNumeric: 'tabular-nums',
          },
          '& .tnum-cell': {fontVariantNumeric: 'tabular-nums'},
        }}
      />
    </>
  );
};

export default NewAccountsPage;
